import moment from 'moment';
import momentTz from 'moment-timezone';

const DATETIME_FORMAT = 'MMM D, h:mm A';
const FULL_DATETIME_FORMAT = 'dddd, MMM D YYYY, h:mm A';
const DATE_FORMAT = 'dddd, MMM D';
const ISO_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

moment.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: 'Now',
    ss: '%ds',
    m: '1m',
    mm: '%dm',
    h: '1h',
    hh: '%dh',
    d: 'a day',
    dd: '%d days',
    M: 'a month',
    MM: '%d months',
    y: 'a year',
    yy: '%d years',
  },
  calendar: {
    sameDay: `[Today], ${DATETIME_FORMAT}`,
    lastDay: `[Yesterday], ${DATETIME_FORMAT}`,
    nextDay: `[Tomorrow], ${DATETIME_FORMAT}`,
    lastWeek: `dddd, ${DATETIME_FORMAT}`,
    nextWeek: `dddd, ${DATETIME_FORMAT}`,
    sameElse: `dddd, ${DATETIME_FORMAT}`,
  },
});

moment.relativeTimeThreshold('m', 60);

export const getFullDateFormat = (unixTime: number) => {
  return moment.unix(unixTime).format(FULL_DATETIME_FORMAT);
};

export const getDateFormat = (unixTime: number) => {
  return moment.unix(unixTime).format(DATE_FORMAT);
};

export function getFormattedDate(unixTime: number) {
  const now = Date.now() / 1000;
  const timeDelta = now - unixTime;

  const oneMinute = 60;
  const fiveHours = 60 * 60 * 5;
  const oneDay = 60 * 60 * 24;
  const oneYear = oneDay * 365;

  // less than 1 minute
  if (
    (timeDelta <= oneMinute && timeDelta >= 0) ||
    (timeDelta >= -1 * oneMinute && timeDelta <= 0)
  ) {
    return 'Now';
  }

  // less than 5 hours
  if (
    (timeDelta <= fiveHours && timeDelta >= 0) ||
    (timeDelta >= -1 * fiveHours && timeDelta <= 0)
  ) {
    return moment.unix(unixTime).from(moment(now * 1000));
  }

  // 5 hours to 1 year
  if (
    (timeDelta <= oneYear && timeDelta >= 0) ||
    (timeDelta >= -1 * oneYear && timeDelta <= 0)
  ) {
    return moment.unix(unixTime).calendar();
  }

  // Over 1 year
  return getFullDateFormat(unixTime);
}

export const getUnixTime = (datetime: string) => {
  return moment(datetime, ISO_DATETIME_FORMAT).unix();
};

export const getZone = () => {
  const zone = momentTz.tz(momentTz.tz.guess(true)).zoneName();
  // If the zone is not registered it will return the time difference, e.g.: -04  for America/Caracas
  if (!isNaN(Number(zone))) {
    const zoneNumber = Number(zone);
    if (zoneNumber > 0) {
      return `GMT+${zoneNumber}`;
    } else if (zoneNumber < 0) {
      return `GMT${zoneNumber}`;
    }
    return `GMT`;
  }
  return zone;
};

export function getFormattedDateWithTimeZone(date: string) {
  const zone = getZone();
  const localTime = momentTz.tz(date, momentTz.tz.guess(true)).unix();
  const formattedLocalTime = getFullDateFormat(localTime);
  return `${formattedLocalTime} (${zone})`;
}
